import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiClock, FiCopy, FiCheck, FiMail } from 'react-icons/fi';

function RateLimitModal({ isOpen, onClose, rateLimitData }) {
  const [copied, setCopied] = useState(false);
  
  // Ref to track if cleanup should actually restore scroll
  const shouldRestoreScrollRef = useRef(true);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      shouldRestoreScrollRef.current = true;
      
      // Get scroll position - use window global if set, otherwise window.scrollY
      let scrollY;
      if (window.__modalScrollY !== undefined) {
        scrollY = window.__modalScrollY;
      } else {
        scrollY = window.scrollY;
        window.__modalScrollY = scrollY;
      }
      
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      
      return () => {
        shouldRestoreScrollRef.current = false;
        
        // Defer the actual cleanup to next tick to handle StrictMode
        setTimeout(() => {
          if (!shouldRestoreScrollRef.current && window.__modalScrollY !== undefined) {
            const savedScrollY = window.__modalScrollY;
            delete window.__modalScrollY;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.overflow = '';
            window.scrollTo(0, savedScrollY);
          }
        }, 0);
      };
    }
  }, [isOpen]);

  if (!isOpen || !rateLimitData) return null;

  const {
    limit = 5,
    reset_in_minutes = 60,
    recent_reference_ids = [],
    contact_email = 'enquiries@haden.in'
  } = rateLimitData;

  const handleCopyAll = () => {
    const refIds = recent_reference_ids.join('\n');
    navigator.clipboard.writeText(refIds);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatTime = (minutes) => {
    if (minutes < 1) return 'less than a minute';
    if (minutes === 1) return '1 minute';
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours} hour${hours > 1 ? 's' : ''} ${mins} minute${mins > 1 ? 's' : ''}`;
  };

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 z-50 flex items-start justify-center pt-28 pb-8 px-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
          style={{ overscrollBehavior: 'contain' }}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-8 text-center">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
              aria-label="Close"
            >
              <FiX className="text-xl" />
            </button>
            
            <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
              <FiClock className="text-3xl text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">
              Submission Limit Reached
            </h2>
            <p className="text-white/90 text-sm">
              You&apos;ve submitted {limit} inquiries in the last hour
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Reference IDs Section */}
            {recent_reference_ids.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">
                    Your Recent Reference IDs
                  </h3>
                  <button
                    onClick={handleCopyAll}
                    className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    {copied ? (
                      <>
                        <FiCheck className="text-green-600" />
                        <span className="text-green-600">Copied!</span>
                      </>
                    ) : (
                      <>
                        <FiCopy />
                        <span>Copy All</span>
                      </>
                    )}
                  </button>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="space-y-2">
                    {recent_reference_ids.map((refId, index) => (
                      <div 
                        key={index}
                        className="flex items-center gap-3"
                      >
                        <span className="w-6 h-6 flex items-center justify-center bg-primary-100 text-primary-700 rounded-full text-xs font-medium">
                          {index + 1}
                        </span>
                        <code className="font-mono text-lg font-semibold tracking-wider" style={{ color: '#101957' }}>
                          {refId}
                        </code>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Wait Time */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <FiClock className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-amber-800 font-medium">
                    Please try again after approximately
                  </p>
                  <p className="text-amber-900 font-bold text-lg">
                    {formatTime(reset_in_minutes)}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="text-center border-t border-gray-100 pt-4">
              <p className="text-gray-600 text-sm mb-2">
                Need immediate assistance?
              </p>
              <a
                href={`mailto:${contact_email}`}
                className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <FiMail />
                {contact_email}
              </a>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 pb-6">
            <button
              onClick={onClose}
              className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

export default RateLimitModal;
